import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select'; 
import * as XLSX from 'xlsx'; 
import '../assets/css/DataChecker.css'; 

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5002' 
  : window.location.origin;
const UPLOAD_URL = `${API_BASE_URL}/api/upload`;

function BulkUploader() {
    // ✅ NAYA: User ki state nikal rahe hain
    const uploaderUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch (error) {
            return {};
        }
    }, []);
    const stateName = uploaderUser?.stateName || 'UP';
    const authHeaders = { headers: { 'x-state-name': stateName } };

    const remarkOptions = [
        { value: 'Approved by nectar infotel', label: 'Approved by nectar infotel' },
        { value: 'Approved by Dccb', label: 'Approved by Dccb' }
    ];

    const [districts, setDistricts] = useState([]);
    const [branches, setBranches] = useState([]);
    const [pacsList, setPacsList] = useState([]);

    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedPacs, setSelectedPacs] = useState(null);
    const [selectedRemark, setSelectedRemark] = useState(null);

    // 🔒 LOCK & REQUEST STATES
    const [isLocked, setIsLocked] = useState(false);
    const [isRequested, setIsRequested] = useState(false);

    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState("No file selected");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    
    // 📊 NEW: Disbursement Stats State & Modal State
    const [fyStats, setFyStats] = useState(null);
    const [showStatsModal, setShowStatsModal] = useState(false);
    
    // UI Filters
    const [viewMode, setViewMode] = useState('ERROR'); 
    const [activeSheet, setActiveSheet] = useState('ALL');
    const [activeError, setActiveError] = useState('ALL');

    useEffect(() => {
        // ✅ NAYA LOGIC: Headers me state bhej rahe hain
        axios.get(`${API_BASE_URL}/api/districts`, authHeaders)
            .then(res => setDistricts(res.data.map(d => ({ value: d.district_id, label: d.district_name }))))
            .catch(err => console.error(err));
    }, [stateName]);

    const handleDistrictChange = (opt) => {
        setSelectedDistrict(opt); setSelectedBranch(null); setSelectedPacs(null); setBranches([]); setPacsList([]); setIsLocked(false);
        // ✅ State pass
        if(opt) axios.get(`${API_BASE_URL}/api/branches/${opt.value}`, authHeaders).then(res => setBranches(res.data.map(b => ({ value: b.branch_id, label: b.branch_name }))));
    };

    const handleBranchChange = (opt) => {
        setSelectedBranch(opt); setSelectedPacs(null); setPacsList([]); setIsLocked(false);
        // ✅ State pass
        if(opt) axios.get(`${API_BASE_URL}/api/pacs/${opt.value}`, authHeaders).then(res => setPacsList(res.data.map(p => ({ value: p.pacs_id, label: p.pacs_name }))));
    };

    // 🔒 PACS SELECTION & STATUS CHECK
    const handlePacsChange = async (opt) => {
        setSelectedPacs(opt);
        setIsLocked(false);
        setIsRequested(false);
        setResult(null); 
        setFyStats(null); 

        if (opt) {
            try {
                // ✅ State pass in body & header
                const res = await axios.post(`${API_BASE_URL}/api/admin/check-status`, { pacs_id: opt.value, stateName }, authHeaders);
                if (res.data.hasData) {
                    setIsLocked(true); 
                    setIsRequested(res.data.isRequested);
                }
            } catch (err) { console.error("Status Check Failed:", err); }
        }
    };

    const handleRequestReupload = async () => {
        if (!selectedPacs) return;
        try {
            // ✅ State pass in body & header
            await axios.post(`${API_BASE_URL}/api/admin/request-reupload`, { pacs_id: selectedPacs.value, stateName }, authHeaders);
            setIsRequested(true);
            alert("✅ Request sent to Super Admin! Please wait for approval.");
        } catch (err) { 
            alert("Error sending request. Please try again."); 
        }
    };

    const handleFileChange = (e) => {
        if(e.target.files[0]) {
            setFile(e.target.files[0]);
            setFileName(e.target.files[0].name);
            setResult(null); 
            setFyStats(null);
        }
    };

    const handleSubmit = async () => {
        if (!selectedRemark) return alert("⚠️ Kripya 'Approval Remark' select karein!");
        if (!file || !selectedPacs) return alert("⚠️ Please select all details and file!");
        if (isLocked) return alert("⛔ Upload is Locked! Please Request Re-upload.");

        setLoading(true); setResult(null); setFyStats(null);
        
        const formData = new FormData();
        formData.append('excelFile', file);
        formData.append('DISTRICT_ID', selectedDistrict.value);
        formData.append('DISTRICT_NAME', selectedDistrict.label);
        formData.append('BRANCH_ID', selectedBranch.value);
        formData.append('PACS_ID', selectedPacs.value); 
        formData.append('PACS_NAME', selectedPacs.label);
        formData.append('REMARK', selectedRemark.value);
        // ✅ BACKEND KO STATE BHEJ RAHE HAIN UPLOAD KE WAQT
        formData.append('STATE_NAME', stateName); 

        try {
            const res = await axios.post(UPLOAD_URL, formData, { headers: { 'Content-Type': 'multipart/form-data', 'x-state-name': stateName } });
            setResult(res.data); 
            
            if (res.data.fyStats) {
                setFyStats(res.data.fyStats);
            }

            setViewMode('ERROR'); 
            setActiveSheet('ALL');
            setActiveError('ALL');
            
            if(res.data.uploaded > 0) {
                setIsLocked(true); 
            }

        } catch (err) {
            alert(err.response?.data?.message || "Upload failed.");
        } finally {
            setLoading(false);
        }
    };

    // --- PARSING & GROUPING LOGIC ---
    const parsedData = useMemo(() => {
        if (!result || !result.errors) return [];
        return result.errors.map(err => {
            const sheet = err.sheet || err.Sheet;
            const row = err.row || err.Excel_Row;
            const admissionNo = err.admissionNo ?? err.AdmissionNo ?? null;
            const column = err.column ?? err.Column ?? null;

            let level = err.level || err.Level || 'ERROR';
            let groupName = err.error || err.Galti || 'Upload Issue';
            let details = err.details || err.Details || '-';

            if (!err.error && typeof groupName === 'string') {
                if (groupName.includes('(OBSERVATION)') || groupName.includes('(WARNING)')) {
                    level = 'WARNING';
                }
                groupName = groupName.replace('(FAIL)', '').replace('(OBSERVATION)', '').replace('(WARNING)', '').replace('(DATA SKIP)', '').trim();
                if (details === '-') details = groupName;
            }

            if (groupName.includes("Unknown Loan") || (groupName.includes("Loan No") && groupName.includes("not in Loans sheet"))) {
                groupName = "Unknown Loan";
                details = details || groupName;
            } 
            else if (groupName.includes("Zero Share") || groupName.includes("Share Balance")) {
                groupName = "Invalid Share Balance";
                details = details || groupName;
            }
            else if (groupName.includes("chk_logic_mismatch") || groupName.includes("DB Error")) {
                groupName = "DB Constraint Error";
                details = details || groupName;
            }
            else if (groupName.includes("Duplicate Entry")) {
                groupName = "Duplicate Entry";
                details = details || groupName;
            }

            return {
                sheet,
                row,
                admissionNo,
                column,
                error: groupName, 
                details: details, 
                level: level
            };
        });
    }, [result]);

    const relevantData = useMemo(() => parsedData.filter(item => item.level === viewMode), [parsedData, viewMode]);
    const sheetList = useMemo(() => ['ALL', ...new Set(relevantData.map(item => item.sheet))], [relevantData]);
    const filteredBySheet = useMemo(() => {
        if (activeSheet === 'ALL') return relevantData;
        return relevantData.filter(item => item.sheet === activeSheet);
    }, [relevantData, activeSheet]);
    const errorSummary = useMemo(() => {
        const summary = {};
        filteredBySheet.forEach(item => {
            summary[item.error] = (summary[item.error] || 0) + 1;
        });
        return summary;
    }, [filteredBySheet]);
    const displayedRows = filteredBySheet.filter(item => activeError === 'ALL' || item.error === activeError);
    const errorCount = parsedData.filter(e => e.level === 'ERROR').length;
    const warningCount = parsedData.filter(e => e.level === 'WARNING').length;

    const handleDownloadLog = () => {
        if(parsedData.length === 0) return alert("No logs.");
        const ws = XLSX.utils.json_to_sheet(parsedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Upload_Log");
        XLSX.writeFile(wb, "Upload_Findings.xlsx");
    };
    const handleDownloadSuccess = () => {
        if (!result || !result.successfulData) return;
        const ws = XLSX.utils.json_to_sheet(result.successfulData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Success_Data");
        XLSX.writeFile(wb, "Success_Report.xlsx");
    };

    return (
        <div className="dc-container">
            <div className="dc-navbar" style={{background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)'}}>
                <div className="dc-content-wrapper" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h1 style={{color: 'white', margin: 0, fontSize: '1.5rem'}}>
                        <i className="fa-solid fa-cloud-arrow-up me-2"></i> PACS Bulk Data Uploader (v3.0)
                    </h1>
                    {/* ✅ NAYA: State Indicator Badge */}
                    <div style={{background: 'rgba(255,255,255,0.2)', color: 'white', padding: '6px 15px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.9rem'}}>
                        <i className="fa-solid fa-map-location-dot me-2"></i> 
                        State: {stateName}
                    </div>
                </div>
            </div>

            <div className="dc-main-container">
                <div className="dc-card">
                    <div className="dc-card-body">
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '25px'}}>
                            <div><label className="dc-filter-label">District</label><Select options={districts} value={selectedDistrict} onChange={handleDistrictChange} placeholder="Select..." /></div>
                            <div><label className="dc-filter-label">Branch</label><Select options={branches} value={selectedBranch} onChange={handleBranchChange} isDisabled={!selectedDistrict} placeholder="Select..." /></div>
                            <div><label className="dc-filter-label">PACS</label><Select options={pacsList} value={selectedPacs} onChange={handlePacsChange} isDisabled={!selectedBranch} placeholder="Select..." /></div>
                            <div><label className="dc-filter-label" style={{color: '#4f46e5'}}>Approval Remark *</label><Select options={remarkOptions} value={selectedRemark} onChange={setSelectedRemark} placeholder="Select..." /></div>
                        </div>

                        {/* 🔒 LOCKED ALERT UI */}
                        {isLocked && !result && ( 
                            <div style={{background: '#fff3cd', color: '#856404', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffeeba', textAlign: 'center'}}>
                                <div style={{fontSize: '1.2rem', marginBottom: '10px', fontWeight: 'bold'}}>
                                    <i className="fa-solid fa-lock me-2"></i> Data Already Exists
                                </div>
                                <p style={{margin: '0 0 15px 0'}}>This PACS has uploaded data. You cannot upload again without approval.</p>
                                
                                {isRequested ? (
                                    <div style={{background: '#d1fae5', color: '#065f46', padding: '10px', borderRadius: '5px', display: 'inline-flex', alignItems: 'center', fontWeight: 'bold'}}>
                                        <i className="fa-solid fa-hourglass-half me-2 fa-spin"></i> Request Pending for Super Admin Approval
                                    </div>
                                ) : (
                                    <button onClick={handleRequestReupload} style={{background: '#d97706', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', boxShadow: '0 2px 5px rgba(0,0,0,0.1)'}}>
                                        <i className="fa-solid fa-paper-plane me-2"></i> Request Re-upload Permission
                                    </button>
                                )}
                            </div>
                        )}

                        {/* HIDE UPLOAD IF LOCKED, BUT KEEP RESULT VISIBLE */}
                        {!isLocked && (
                            <div className="dc-upload-area" onClick={() => document.getElementById('fileInput').click()}>
                                <i className="fa-solid fa-file-excel dc-upload-icon"></i>
                                <div className="dc-upload-text">{fileName}</div>
                                <input id="fileInput" type="file" onChange={handleFileChange} accept=".xlsx, .xls" style={{display: 'none'}} />
                            </div>
                        )}

                        <button className="dc-btn-primary" onClick={handleSubmit} disabled={loading || !file || !selectedPacs || isLocked} style={{marginTop: '20px', width: '100%', opacity: isLocked ? 0.5 : 1, cursor: isLocked ? 'not-allowed' : 'pointer'}}>
                            {loading ? <><i className="fa-solid fa-spinner fa-spin me-2"></i> Processing Upload...</> : isLocked ? "🚫 Upload Locked (Data Exists)" : <><i className="fa-solid fa-rocket me-2"></i> Start Data Upload</>}
                        </button>
                    </div>
                </div>

                {/* RESULT TABLE */}
                {result && (
                    <div style={{marginTop: '30px'}}>
                         <div style={{display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px', flexWrap: 'wrap'}}>
                            <div className="dc-card" style={{borderLeft: '5px solid #28a745', padding: '20px', minWidth: '200px', textAlign: 'center'}}>
                                <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#28a745'}}>{result.uploaded}</div>
                                <div style={{color: '#6c757d', fontWeight: '600'}}>Successful Rows</div>
                            </div>
                            <div className="dc-card" style={{borderLeft: '5px solid #dc3545', padding: '20px', minWidth: '200px', textAlign: 'center'}}>
                                <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#dc3545'}}>{errorCount}</div>
                                <div style={{color: '#6c757d', fontWeight: '600'}}>Rejected Rows</div>
                            </div>
                        </div>

                        <div className="dc-card">
                            <div className="dc-card-header">
                                <h3><i className="fa-solid fa-list-check me-2"></i> Validation Findings</h3>
                                <div style={{display: 'flex', gap: '10px'}}>
                                    {fyStats && (
                                        <button onClick={() => setShowStatsModal(true)} className="dc-btn-primary" style={{backgroundColor: '#6f42c1'}}>
                                            <i className="fa-solid fa-chart-simple me-2"></i> Disbursement Stats
                                        </button>
                                    )}

                                    {result.uploaded > 0 && <button onClick={handleDownloadSuccess} className="dc-btn-primary" style={{backgroundColor: '#28a745'}}><i className="fa-solid fa-download"></i> Success Data</button>}
                                    <button onClick={handleDownloadLog} className="dc-btn-warning"><i className="fa-solid fa-download"></i> Full Log</button>
                                </div>
                            </div>
                            
                            <div className="dc-card-body">
                                <div style={{display: 'flex', gap: '15px', marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '10px'}}>
                                    <button onClick={() => { setViewMode('ERROR'); setActiveSheet('ALL'); setActiveError('ALL'); }}
                                        style={{flex: 1, padding: '12px', border: 'none', borderRadius: '8px', cursor: 'pointer', backgroundColor: viewMode === 'ERROR' ? '#dc3545' : '#f8f9fa', color: viewMode === 'ERROR' ? 'white' : '#6c757d', fontWeight: 'bold', boxShadow: viewMode === 'ERROR' ? '0 4px 6px rgba(220, 53, 69, 0.2)' : 'none'}}>
                                        <i className="fa-solid fa-circle-xmark me-2"></i> Strict Errors (Rejects) <span style={{backgroundColor: 'rgba(255,255,255,0.3)', padding: '2px 8px', borderRadius: '10px', fontSize: '0.8rem', marginLeft: '5px'}}>{errorCount}</span>
                                    </button>
                                    <button onClick={() => { setViewMode('WARNING'); setActiveSheet('ALL'); setActiveError('ALL'); }}
                                        style={{flex: 1, padding: '12px', border: 'none', borderRadius: '8px', cursor: 'pointer', backgroundColor: viewMode === 'WARNING' ? '#ffc107' : '#f8f9fa', color: viewMode === 'WARNING' ? '#212529' : '#6c757d', fontWeight: 'bold', boxShadow: viewMode === 'WARNING' ? '0 4px 6px rgba(255, 193, 7, 0.2)' : 'none'}}>
                                        <i className="fa-solid fa-eye me-2"></i> Observations (Uploaded) <span style={{backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 8px', borderRadius: '10px', fontSize: '0.8rem', marginLeft: '5px'}}>{warningCount}</span>
                                    </button>
                                </div>

                                <div className="dc-filter-section">
                                    <div className="dc-filter-group">
                                        <label className="dc-filter-label">Filter by Sheet</label>
                                        <div className="dc-filter-row">
                                            {sheetList.length > 0 ? sheetList.map(sheet => (
                                                <button key={sheet} onClick={() => { setActiveSheet(sheet); setActiveError('ALL'); }} className={`dc-filter-btn sheet-btn ${activeSheet === sheet ? 'active' : ''}`}>{sheet}</button>
                                            )) : <span style={{color:'#999'}}>No findings.</span>}
                                        </div>
                                    </div>
                                    {sheetList.length > 0 && (
                                        <div className="dc-filter-group">
                                            <label className="dc-filter-label">Filter by Issue</label>
                                            <div className="dc-filter-row">
                                                <button onClick={() => setActiveError('ALL')} className={`dc-filter-btn ${activeError === 'ALL' ? 'active' : ''}`}>Show All</button>
                                                {Object.keys(errorSummary).map(err => (
                                                    <button key={err} onClick={() => setActiveError(err)} className={`dc-filter-btn ${activeError === err ? 'active' : ''}`}>{err} ({errorSummary[err]})</button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="dc-table-container">
                                    <table className="dc-table">
                                        <thead><tr><th>Sheet</th><th>Row</th><th>Admission No</th><th>Column</th><th>Issue</th><th>Details</th></tr></thead>
                                        <tbody>
                                            {displayedRows.length > 0 ? displayedRows.map((item, index) => (
                                                <tr key={index}>
                                                    <td><span className="dc-sheet-name">{item.sheet}</span></td>
                                                    <td><span className="dc-row-badge">Row {item.row}</span></td>
                                                    <td><span className="dc-col-badge">{item.admissionNo || 'N/A'}</span></td>
                                                    <td><span className="dc-col-badge">{item.column || 'N/A'}</span></td>
                                                    <td><span style={{color: viewMode === 'WARNING' ? '#856404' : '#dc3545', fontWeight: 'bold'}}>{item.error}</span></td>
                                                    <td style={{fontSize: '0.9rem', color: '#555'}}>{item.details}</td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="6" style={{textAlign: 'center', padding: '40px', color: '#6c757d'}}><i className="fa-solid fa-check-circle" style={{fontSize: '3rem', marginBottom: '10px', color: '#28a745'}}></i><div style={{fontSize: '1.2rem'}}>No {viewMode === 'ERROR' ? 'Strict Errors' : 'Observations'} found.</div></td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {/* 📊 STATS POPUP MODAL */}
            {showStatsModal && fyStats && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', 
                    background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: 'white', padding: '30px', borderRadius: '12px', width: '400px', 
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)', textAlign: 'center', position: 'relative'
                    }}>
                        <h2 style={{color: '#4f46e5', marginBottom: '20px', borderBottom: '2px solid #eee', paddingBottom: '10px'}}>
                            Loan Disbursement Stats
                        </h2>
                        
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                            <span style={{fontWeight: 'bold', color: '#555'}}>FY 2023-2024:</span>
                            <span style={{fontWeight: 'bold', color: '#28a745', fontSize: '1.2rem'}}>{fyStats['2023-2024']}</span>
                        </div>
                        
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '25px', padding: '10px', background: '#f8f9fa', borderRadius: '8px'}}>
                            <span style={{fontWeight: 'bold', color: '#555'}}>FY 2024-2025:</span>
                            <span style={{fontWeight: 'bold', color: '#28a745', fontSize: '1.2rem'}}>{fyStats['2024-2025']}</span>
                        </div>

                        <button onClick={() => setShowStatsModal(false)} 
                            style={{
                                background: '#dc3545', color: 'white', border: 'none', padding: '10px 25px', 
                                borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem'
                            }}>
                            Close
                        </button>
                    </div>
                </div>
            )}

            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css" />
        </div>
    );
}

export default BulkUploader;
